import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarDays, Cpu } from "lucide-react";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toDate = (v) => (v ? new Date(`${v}T00:00:00`) : undefined);
const DatePick = ({ label, value, onChange, tid }) => {
  const [open, setOpen] = useState(false);
  return (
  <div className="text-xs text-gray-500">{label}
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 border rounded-lg px-2 py-1.5 text-sm text-[#0A0A0A] min-w-[130px] bg-white" data-testid={tid}>
          <CalendarDays size={14} /> {value ? format(toDate(value), "dd/MM/yyyy") : "gg/mm/aaaa"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" locale={it} selected={toDate(value)} initialFocus
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
        {value && <button className="w-full text-xs py-2 border-t text-gray-600" onClick={() => { onChange(""); setOpen(false); }} data-testid={`${tid}-clear`}>Cancella data</button>}
      </PopoverContent>
    </Popover>
  </div>
  );
};

const ND = <span className="text-gray-400">Non disponibile</span>;
const usd = (v) => (v == null ? ND : `${v.toLocaleString("it-IT", { minimumFractionDigits: 4, maximumFractionDigits: 6 })} $`);
const int = (v) => (v == null ? ND : v.toLocaleString("it-IT"));
const when = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const OUTCOME = { ok: "Completata", limitata: "Bloccata dal limite", errore: "Errore", timeout: "Timeout", degenere: "Non valida" };

const Filters = ({ data, f, setF }) => (
  <div className="flex flex-wrap gap-3 items-end" data-testid="pa-ai-filters">
    <label className="text-xs text-gray-500">Property Advisor
      <select className="block border rounded-lg px-2 py-1.5 text-sm" value={f.pa_id} onChange={(e) => setF({ ...f, pa_id: e.target.value })} data-testid="pa-ai-filter-pa">
        <option value="">Tutti</option>
        {(data?.pas || []).map((p) => <option key={p.pa_id} value={p.pa_id}>{p.name}</option>)}
      </select>
    </label>
    <label className="text-xs text-gray-500">Funzione
      <select className="block border rounded-lg px-2 py-1.5 text-sm" value={f.function} onChange={(e) => setF({ ...f, function: e.target.value })} data-testid="pa-ai-filter-function">
        <option value="">Tutte</option>
        {(data?.functions || []).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
      </select>
    </label>
    <DatePick label="Dal" value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} tid="pa-ai-filter-from" />
    <DatePick label="Al" value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} tid="pa-ai-filter-to" />
  </div>
);

export default function AdminPaAiUsage() {
  const [f, setF] = useState({ pa_id: "", function: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    const ctrl = new AbortController();
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/admin/pa-ai-usage", { params, signal: ctrl.signal }).then((r) => { setData(r.data); setError(null); })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        const d = e?.response?.data?.detail;
        setError(typeof d === "string" ? d : d?.message || "Caricamento non riuscito");
      });
    return () => ctrl.abort();
  }, [f]);

  return (
    <div className="space-y-6" data-testid="admin-pa-ai-usage">
      <div className="flex items-center gap-2"><Cpu className="text-[#B40101]" size={22} /><h1 className="text-3xl font-bold text-[#0A0A0A]">Consumi AI Property Advisor</h1></div>
      <p className="text-sm text-gray-500">Sola lettura. Chiamate AI avviate dai Property Advisor in approfondimenti e confronti. Nessun prezzo, credito o addebito.</p>
      <Filters data={data} f={f} setF={setF} />
      {error && <p className="text-sm text-red-700" data-testid="pa-ai-error">{error}</p>}
      {data && (
        <>
          <div className="flex gap-6 text-sm" data-testid="pa-ai-summary">
            <span>Chiamate: <strong data-testid="pa-ai-count">{data.count}</strong></span>
            <span>Costo registrato nel periodo: <strong data-testid="pa-ai-total">{usd(data.total_cost_usd)}</strong></span>
            {data.missing_cost > 0 && <span className="text-gray-500" data-testid="pa-ai-missing">{data.missing_cost} senza costo disponibile</span>}
            {data.shown < data.count && <span className="text-gray-500">mostrate le ultime {data.shown}</span>}
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm" data-testid="pa-ai-table">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>{["PA", "Caso", "Funzione", "Data e ora", "Modello", "Token in", "Token out", "Costo", "Cache", "Esito"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.items.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100" data-testid={`pa-ai-row-${i}`}>
                    <td className="px-3 py-2">{r.pa_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.case_id || ND}</td>
                    <td className="px-3 py-2">{r.function_label}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{when(r.created_at)}</td>
                    <td className="px-3 py-2">{r.model || ND}</td>
                    <td className="px-3 py-2">{int(r.input_tokens)}</td>
                    <td className="px-3 py-2">{int(r.output_tokens)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{usd(r.cost_usd)}</td>
                    <td className="px-3 py-2">{r.cache === "hit" ? "Riutilizzata" : "Nuova"}</td>
                    <td className="px-3 py-2" data-testid={`pa-ai-outcome-${i}`}>{OUTCOME[r.outcome] || r.outcome || ND}</td>
                  </tr>
                ))}
                {!data.items.length && <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-500" data-testid="pa-ai-empty">Nessuna chiamata registrata.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
